import React, { useEffect } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import {
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  AutoStories as GuidesIcon,
  Calculate as CalculateIcon,
  CheckCircleOutline as CheckIcon,
  Hub as PedigreeIcon,
  MenuBook as LearnIcon,
  OpenInNew as ExternalIcon,
  ScienceOutlined as ScienceIcon
} from '@mui/icons-material';
import pigeonIcon from '../assets/icons/pigeon_icon.png';
import { articleCards, articles } from '../content/educationArticles';

const SITE_URL = 'https://app.pigeonpedigreemanager.com';

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);
  const existed = Boolean(element);
  if (!element) {
    element = document.createElement('meta');
    if (selector.includes('property=')) element.setAttribute('property', selector.match(/property="([^"]+)/)[1]);
    else element.setAttribute('name', selector.match(/name="([^"]+)/)[1]);
    document.head.appendChild(element);
  }
  const previous = element.getAttribute(attribute);
  element.setAttribute(attribute, value);
  return { element, existed, previous, attribute };
};

const useSeo = ({ title, description, path, type = 'website', structuredData }) => {
  useEffect(() => {
    const fullTitle = `${title} | Pigeon Pedigree Manager Pro`;
    const canonicalUrl = `${SITE_URL}${path}`;
    const previousTitle = document.title;
    document.title = fullTitle;
    const previousMetas = [
      setMeta('meta[name="description"]', 'content', description),
      setMeta('meta[property="og:title"]', 'content', fullTitle),
      setMeta('meta[property="og:description"]', 'content', description),
      setMeta('meta[property="og:type"]', 'content', type),
      setMeta('meta[property="og:url"]', 'content', canonicalUrl)
    ];

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalExisted = Boolean(canonical);
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const previousCanonical = canonical.getAttribute('href');
    canonical.setAttribute('href', canonicalUrl);

    const script = document.createElement('script');
    script.id = 'page-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData || {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: canonicalUrl
    });
    document.getElementById(script.id)?.remove();
    document.head.appendChild(script);

    return () => {
      script.remove();
      document.title = previousTitle;
      previousMetas.forEach(({ element, existed, previous, attribute }) => {
        if (!existed) element.remove();
        else if (previous === null) element.removeAttribute(attribute);
        else element.setAttribute(attribute, previous);
      });
      if (!canonicalExisted) canonical.remove();
      else if (previousCanonical === null) canonical.removeAttribute('href');
      else canonical.setAttribute('href', previousCanonical);
    };
  }, [title, description, path, type, structuredData]);
};

const PublicHeader = () => (
  <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
    <Container maxWidth="lg">
      <Toolbar disableGutters sx={{ minHeight: { xs: 64, sm: 72 }, gap: 2 }}>
        <Box component={RouterLink} to="/resources" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: 'inherit', textDecoration: 'none', minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <img src={pigeonIcon} alt="" width="27" height="27" />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Pigeon Pedigree</Typography>
            <Typography sx={{ color: 'primary.main', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}>Learn &amp; Tools</Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button component={RouterLink} to="/resources" sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Guides</Button>
          <Button href="https://pigeonpedigreemanager.com/tools/velocity-calculator" startIcon={<CalculateIcon />} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
            Velocity calculator
          </Button>
          <Button component={RouterLink} to="/" variant="contained">Open app</Button>
        </Box>
      </Toolbar>
    </Container>
  </AppBar>
);

const PublicFooter = () => (
  <Box component="footer" sx={{ mt: 8, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
    <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary">© {new Date().getFullYear()} Pigeon Pedigree Manager Pro</Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Link component={RouterLink} to="/resources" underline="hover">Guides</Link>
        <Link href="https://pigeonpedigreemanager.com/tools/velocity-calculator" underline="hover">Calculator</Link>
        <Link href="https://pigeonpedigreemanager.com" underline="hover">Product website</Link>
      </Box>
    </Container>
  </Box>
);

const PublicLayout = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
    <PublicHeader />
    <Box component="main" id="main-content" sx={{ flex: 1 }}>{children}</Box>
    <PublicFooter />
  </Box>
);

const cardIcons = [<PedigreeIcon />, <ScienceIcon />, <LearnIcon />, <GuidesIcon />];

export const ResourceHub = () => {
  const description = 'Practical pigeon breeding guides covering pedigrees, genetics, color inheritance and loft selection, plus a free racing velocity calculator.';
  useSeo({ title: 'Pigeon breeding guides and racing tools', description, path: '/resources' });

  return (
    <PublicLayout>
      <Box sx={{ bgcolor: 'primary.darker', color: 'primary.contrastText', py: { xs: 7, md: 10 }, position: 'relative', overflow: 'hidden' }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip label="Free breeder resources" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', fontWeight: 700, mb: 2 }} />
          <Typography component="h1" variant="h2" sx={{ maxWidth: 760, color: 'inherit', fontSize: { xs: '2.25rem', sm: '3rem', md: '3.65rem' }, lineHeight: 1.08 }}>
            Make breeding decisions from records, not memory.
          </Typography>
          <Typography variant="h6" component="p" sx={{ mt: 2, maxWidth: 700, color: 'rgba(255,255,255,0.76)', fontWeight: 400, lineHeight: 1.6 }}>
            Plain-language guides to pigeon genetics, planned breeding and loft development—connected to tools that make the family tree easier to see.
          </Typography>
          <Button href="https://pigeonpedigreemanager.com/tools/velocity-calculator" variant="contained" color="secondary" size="large" startIcon={<CalculateIcon />} sx={{ mt: 3 }}>
            Calculate race velocity
          </Button>
        </Container>
        <Box aria-hidden="true" sx={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', right: -100, top: -180, border: '90px solid rgba(255,255,255,0.035)' }} />
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: 700, mb: 4 }}>
          <Typography variant="overline" color="primary.main">Knowledge center</Typography>
          <Typography component="h2" variant="h3" sx={{ mt: 0.75, fontSize: { xs: '1.9rem', sm: '2.35rem' } }}>Practical guides for a better-recorded loft</Typography>
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>Start with the question you are facing now. Each guide includes concrete checks you can apply to your own pedigree records.</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2.5 }}>
          {articleCards.map((article, index) => (
            <Card key={article.slug} sx={{ height: '100%' }}>
              <CardActionArea component={RouterLink} to={`/guides/${article.slug}`} sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: index === 2 ? 'rgba(207,61,129,0.10)' : 'primary.lighter', color: index === 2 ? 'secondary.main' : 'primary.main', mb: 2 }}>
                    {cardIcons[index]}
                  </Box>
                  <Typography variant="overline" color="primary.main">{article.category} · {article.readTime}</Typography>
                  <Typography component="h3" variant="h5" sx={{ mt: 0.75 }}>{article.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1.25, mb: 2.5 }}>{article.description}</Typography>
                  <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, color: 'primary.main', fontWeight: 700 }}>
                    Read guide <ArrowForwardIcon fontSize="small" />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        <Paper sx={{ mt: 7, p: { xs: 3, md: 5 }, borderRadius: 3.5, bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light', boxShadow: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 3, alignItems: 'center' }}>
          <Box>
            <Typography variant="overline" color="primary.dark">From theory to your race sheet</Typography>
            <Typography component="h2" variant="h4" sx={{ mt: 0.5 }}>Calculate YPM or MPM in seconds</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 650 }}>Use distance and elapsed flight time to get average velocity in both common racing units, with km/h and mph for reference.</Typography>
          </Box>
          <Button href="https://pigeonpedigreemanager.com/tools/velocity-calculator" variant="contained" size="large" endIcon={<ArrowForwardIcon />}>Open calculator</Button>
        </Paper>
      </Container>
    </PublicLayout>
  );
};

const ArticleBreadcrumbs = ({ title }) => (
  <Breadcrumbs aria-label="Breadcrumb" sx={{ mb: 3 }}>
    <Link component={RouterLink} to="/resources" underline="hover">Resources</Link>
    <Typography color="text.secondary">{title}</Typography>
  </Breadcrumbs>
);

export const EducationArticle = () => {
  const { slug } = useParams();
  const article = articles[slug];
  const structuredData = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    mainEntityOfPage: `${SITE_URL}/guides/${slug}`,
    author: { '@type': 'Organization', name: 'Pigeon Pedigree Manager Pro' },
    publisher: { '@type': 'Organization', name: 'Pigeon Pedigree Manager Pro', url: 'https://pigeonpedigreemanager.com' }
  } : undefined;
  useSeo({
    title: article?.title || 'Guide not found',
    description: article?.description || 'The requested pigeon breeding guide could not be found.',
    path: `/guides/${slug}`,
    type: 'article',
    structuredData
  });

  if (!article) return <Navigate to="/resources" replace />;

  return (
    <PublicLayout>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
        <ArticleBreadcrumbs title={article.title} />
        <Box component="article">
          <Typography variant="overline" color="primary.main">{article.category} · {article.readTime}</Typography>
          <Typography component="h1" variant="h2" sx={{ mt: 1, fontSize: { xs: '2.2rem', sm: '3.15rem' }, lineHeight: 1.1 }}>{article.title}</Typography>
          <Typography variant="h6" component="p" color="text.secondary" sx={{ mt: 2.5, fontWeight: 400, lineHeight: 1.7 }}>{article.intro}</Typography>

          {article.comparison && (
            <TableContainer component={Paper} sx={{ mt: 5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <Table aria-label="Comparison of pigeon breeding methods">
                <TableHead>
                  <TableRow>
                    <TableCell>Method</TableCell>
                    <TableCell>Typical pairing</TableCell>
                    <TableCell>Primary aim</TableCell>
                    <TableCell>Watch for</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {article.comparison.map((row) => (
                    <TableRow key={row.method}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 700, minWidth: 130 }}>{row.method}</TableCell>
                      <TableCell>{row.pairing}</TableCell>
                      <TableCell>{row.aim}</TableCell>
                      <TableCell>{row.watch}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 5 }}>
            {article.sections.map((section) => (
              <Box component="section" key={section.heading} sx={{ mb: 5 }}>
                <Typography component="h2" variant="h4" sx={{ mb: 2 }}>{section.heading}</Typography>
                {section.paragraphs.map((paragraph) => (
                  <Typography key={paragraph} sx={{ mb: 2, fontSize: '1.05rem', lineHeight: 1.78, color: 'text.secondary' }}>{paragraph}</Typography>
                ))}
                {section.checklist && (
                  <Paper sx={{ mt: 3, p: { xs: 2.5, sm: 3 }, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                    <Typography component="h3" variant="h6" sx={{ mb: 2 }}>Practical checklist</Typography>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.5 }}>
                      {section.checklist.map((item) => (
                        <Box component="li" key={item} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                          <CheckIcon aria-hidden="true" sx={{ color: 'success.main', fontSize: 21, mt: '2px', flexShrink: 0 }} />
                          <Typography color="text.secondary">{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 5 }} />
          <Box component="aside" aria-labelledby="sources-title">
            <Typography id="sources-title" component="h2" variant="h5">Research sources and further reading</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>These breeder-focused explanations are educational and summarize the following scientific literature.</Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0, display: 'grid', gap: 1.25 }}>
              {article.sources.map((source) => (
                <Typography component="li" key={source.href} color="text.secondary">
                  <Link href={source.href} target="_blank" rel="noreferrer" underline="hover">{source.label} <ExternalIcon sx={{ fontSize: 14, verticalAlign: '-2px' }} /></Link>
                </Typography>
              ))}
            </Box>
          </Box>

          <Paper sx={{ mt: 6, p: { xs: 3, sm: 4 }, bgcolor: 'primary.darker', color: 'white', borderRadius: 3.5 }}>
            <Typography component="h2" variant="h4" color="inherit">See the family before you plan the pairing.</Typography>
            <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.75)', maxWidth: 650 }}>Build multi-generation pedigrees, spot common ancestors and keep race and breeding records connected to the right bird.</Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button component={RouterLink} to="/" variant="contained" color="secondary">Open the app</Button>
              <Button component={RouterLink} to="/resources" variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.45)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.06)' } }}>Read another guide</Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </PublicLayout>
  );
};
